// ACLS / critical care infusion drugs.
// Each drug declares its concentration default, target dose default, dose unit
// and formula type. The calculator does NOT cap doses — clinicians must verify.
//
// Formulas (rate in cc/hr):
//   mcgPerKgPerMin : (target × weight × 60) / (strength_mg × 1000 / volume_cc)
//   mcgPerMin      : (target × 60)         / (strength_mg × 1000 / volume_cc)
//   mgPerHr        : (target)              / (strength_mg / volume_cc)
//   mgPerKgPerHr   : (target × weight)     / (strength_mg / volume_cc)
//   mgPerMin       : (target × 60)         / (strength_mg / volume_cc)

export const EMERGENCY_DRUGS = [
  {
    id: "epinephrine",
    name: "Epinephrine (Adrenaline)",
    diluent: "D5W or NS 0.9%",
    defaultStrengthMg: 4,
    doseUnit: "mcg/kg/min",
    defaultDose: 0.05,
    doseRange: [0.01, 1.0],
    doseStep: 0.01,
    formula: "mcgPerKgPerMin",
    notes:
      "Post-resus / shock. Titrate to MAP ≥ 65 mmHg. Typical 0.05–0.5 mcg/kg/min. Central line preferred.",
  },
  {
    id: "norepinephrine",
    name: "Norepinephrine",
    diluent: "NS 0.9% preferred · D5W alternative",
    defaultStrengthMg: 4,
    doseUnit: "mcg/kg/min",
    defaultDose: 0.05,
    doseRange: [0.01, 2.0],
    doseStep: 0.01,
    formula: "mcgPerKgPerMin",
    notes:
      "First-line vasopressor in septic shock. Typical 0.05–0.5 mcg/kg/min. Central line preferred.",
  },
  {
    id: "dopamine",
    name: "Dopamine",
    diluent: "D5W or NS 0.9%",
    defaultStrengthMg: 200,
    doseUnit: "mcg/kg/min",
    defaultDose: 5,
    doseRange: [2, 20],
    doseStep: 0.5,
    formula: "mcgPerKgPerMin",
    notes:
      "Renal 2–5, β 5–10, α >10 mcg/kg/min. Largely replaced by norepinephrine in septic shock.",
  },
  {
    id: "dobutamine",
    name: "Dobutamine",
    diluent: "D5W or NS 0.9%",
    defaultStrengthMg: 250,
    doseUnit: "mcg/kg/min",
    defaultDose: 5,
    doseRange: [2.5, 20],
    doseStep: 0.5,
    formula: "mcgPerKgPerMin",
    notes:
      "Inotropic support in cardiogenic shock / decompensated HF. Typical 2.5–10 mcg/kg/min.",
  },
  {
    id: "amiodarone",
    name: "Amiodarone",
    diluent: "Strictly D5W · avoid NS (precipitation)",
    diluentWarn: true,
    defaultStrengthMg: 150,
    doseUnit: "mg/min",
    defaultDose: 1,
    doseRange: [0.5, 1],
    doseStep: 0.1,
    formula: "mgPerMin",
    notes:
      "Post-load: 1 mg/min × 6 h then 0.5 mg/min × 18 h. Total ≈ 1 g over 24 h. Not weight-based.",
  },
  {
    id: "atropine",
    name: "Atropine (infusion estimate)",
    diluent: "Undiluted IV bolus (standard route)",
    diluentWarn: true,
    defaultStrengthMg: 1,
    doseUnit: "mcg/kg/min",
    defaultDose: 0.5,
    doseRange: [0.1, 1.0],
    doseStep: 0.05,
    formula: "mcgPerKgPerMin",
    notes:
      "Usually given as bolus (0.5–1 mg IV q3–5 min, max 3 mg). Infusion off-label — verify locally.",
  },
  {
    id: "propofol",
    name: "Propofol",
    diluent: "Undiluted · ready-to-use lipid emulsion",
    diluentWarn: true,
    defaultStrengthMg: 200,
    doseUnit: "mcg/kg/min",
    defaultDose: 25,
    doseRange: [10, 75],
    doseStep: 1,
    formula: "mcgPerKgPerMin",
    notes:
      "ICU sedation 25–75 mcg/kg/min. Watch for hypotension and propofol-infusion syndrome (>4 mg/kg/h × 48 h).",
  },
  {
    id: "ketamine",
    name: "Ketamine",
    diluent: "D5W or NS 0.9%",
    defaultStrengthMg: 500,
    doseUnit: "mg/kg/hr",
    defaultDose: 0.5,
    doseRange: [0.1, 2.0],
    doseStep: 0.1,
    formula: "mgPerKgPerHr",
    notes:
      "Analgesia: 0.1–0.5 mg/kg/h. Sedation: 0.5–2 mg/kg/h. Bronchodilator and hemodynamically stable.",
  },
  {
    id: "midazolam",
    name: "Midazolam",
    diluent: "D5W or NS 0.9%",
    defaultStrengthMg: 15,
    doseUnit: "mg/kg/hr",
    defaultDose: 0.05,
    doseRange: [0.02, 0.4],
    doseStep: 0.01,
    formula: "mgPerKgPerHr",
    notes:
      "Sedation 0.02–0.1 mg/kg/h (titrate). Accumulates in renal/hepatic dysfunction.",
  },
  {
    id: "nicardipine",
    name: "Nicardipine",
    diluent: "NS 0.9% or D5W",
    defaultStrengthMg: 10,
    // Drug supports two dosing strategies; user picks one via UI toggle.
    doseUnits: [
      {
        unit: "mcg/kg/min",
        formula: "mcgPerKgPerMin",
        default: 1,
        range: [0.5, 5],
        step: 0.1,
      },
      {
        unit: "mg/hr",
        formula: "mgPerHr",
        default: 5,
        range: [2, 15],
        step: 0.5,
      },
    ],
    // Default unit when first selected
    doseUnit: "mg/hr",
    defaultDose: 5,
    doseRange: [2, 15],
    doseStep: 0.5,
    formula: "mgPerHr",
    notes:
      "Hypertensive emergency. Start 5 mg/h, titrate 2.5 mg/h every 5–15 min, max 15 mg/h. Or 0.5–5 mcg/kg/min weight-based. Central or large peripheral line.",
  },
];

export const SYRINGE_VOLUMES = [20, 50];

export function calculateInfusion({ weight, strengthMg, volumeCc, dose, formula }) {
  const w = parseFloat(weight);
  const s = parseFloat(strengthMg);
  const v = parseFloat(volumeCc);
  const d = parseFloat(dose);
  if (!s || !v || !d) return null;
  if (formula.includes("PerKg") && (!w || w <= 0)) return null;

  const concMgPerCc = s / v;
  const concMcgPerCc = concMgPerCc * 1000;

  let rateCcPerHr = 0;
  switch (formula) {
    case "mcgPerKgPerMin":
      rateCcPerHr = (d * w * 60) / concMcgPerCc;
      break;
    case "mcgPerMin":
      rateCcPerHr = (d * 60) / concMcgPerCc;
      break;
    case "mgPerMin":
      rateCcPerHr = (d * 60) / concMgPerCc;
      break;
    case "mgPerHr":
      rateCcPerHr = d / concMgPerCc;
      break;
    case "mgPerKgPerHr":
      rateCcPerHr = (d * w) / concMgPerCc;
      break;
    case "mcgPerKgPerHr":
      rateCcPerHr = (d * w) / concMcgPerCc;
      break;
    default:
      return null;
  }

  return { concMgPerCc, concMcgPerCc, rateCcPerHr };
}
